/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC } from "react";
import { Box, styled } from "@mui/material";
import { getStartTimeOfTask, getPointDescription, CustomDivider } from "./Util";
import { useTaskDataContext } from "../../context/TaskDataProvider";
import { useMapDataContext } from "../../context/MapDataProvider";
import { PlanJobInfoVehicleBase } from "client/models/PlanJobInfoVehicleBase";
import { useTranslation } from "react-i18next";
import { DisplayItem } from "components/Common/DisplayItem";
import { DisplayItemWithTimeIcon } from "./DisplayItemWithTimeIcon";
import { TaskControlMode } from "./TaskControlDialog";

interface ConfirmPanelProps {
  mode: TaskControlMode;
}

export const ConfirmPanel: FC<ConfirmPanelProps> = ({ mode }) => {
  const { t } = useTranslation();
  const { taskData } = useTaskDataContext();
  const { mapData } = useMapDataContext();

  return (
    <Container>
      <ContentsSection>
        <DisplayItem title={t("AWVUI-MSG36")} value={taskData.jobSetName!} />
        <CustomDivider />
        <DisplayItemWithTimeIcon
          isAutoStart={!!taskData.extensionTimeLoad}
          title={t("AWVUI-MSG37")}
          value={getPointDescription(
            taskData.startNodeId!,
            mapData,
            t("AWVUI-MSG119")
          )}
        />
        {taskData.jobInfos?.map(
          (item: PlanJobInfoVehicleBase, index: number) => (
            <DisplayItemWithTimeIcon
              key={`item-${index}`}
              isAutoStart={!!item.extensionTimeUnload}
              title={t("AWVUI-MSG109")}
              value={getPointDescription(
                item.unloadingNodeId,
                mapData,
                t("AWVUI-MSG119")
              )}
            />
          )
        )}
        <DisplayItemWithTimeIcon
          isAutoStart={false}
          title={t("AWVUI-MSG38")}
          value={getPointDescription(
            taskData.endNodeId!,
            mapData,
            t("AWVUI-MSG119")
          )}
        />
        <CustomDivider />
        <DisplayItem
          title={t("AWVUI-MSG39")}
          value={taskData.planTaskExecutionTime + " " + t("AWVUI-MSG54")}
        />
      </ContentsSection>
      <ContentsSection>
        <DisplayItem
          title={t("AWVUI-MSG108")}
          value={getStartTimeOfTask(taskData.startDatetime, t("AWVUI-MSG59"))}
        />
        <DisplayItem title={t("AWVUI-MSG41")} value={taskData.memo ?? ""} />
        {mode === "register" && (
          <DisplayItem
            title={t("AWVUI-MSG167")}
            value={taskData.moveToStart ? t("AWVUI-MSG168") : t("AWVUI-MSG169")}
          />
        )}
      </ContentsSection>
    </Container>
  );
};

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
}));

const ContentsSection = styled(Box)(({ theme }) => ({
  width: "100%",
  borderBottom: "1px solid black",
  padding: "0.5rem 2rem",
}));
